import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '../../store';
import { ContentType } from '../model/tab.interface';
import { postAdapterSelectors } from './perks.reducer';

export const selectPerksState = (state: RootState) => state.perks;

export const selectPerks = createSelector(selectPerksState, (state) => state.perks.value);

export const selectContentTabs = createSelector(
  selectPerksState,
  (state) => state.contentTabs.value ?? []
);
export const selectContentTabsLoading = createSelector(
  selectPerksState,
  (state) => state.contentTabs.loading
);
export const selectContentTabsLoaded = createSelector(
  selectPerksState,
  (state) => state.contentTabs.loaded
);

export const selectPerksLoading = createSelector(
  selectPerksState,
  (state) => state.contentTabs.loading || state.perks.loading
);
export const selectPerksLoaded = selectContentTabsLoaded;

export const selectLoginTypes = createSelector(selectPerksState, (state) => state.loginTypes);

export const selectUser = createSelector(selectPerksState, (state) => state.user.value);
export const selectUserLoading = createSelector(selectPerksState, (state) => state.user.loading);
export const selectUserLoaded = createSelector(selectPerksState, (state) => state.user.loaded);
export const selectUserUpdating = createSelector(
  selectPerksState,
  (state) => state.updatingUser.loading
);
export const selectUserUpdated = createSelector(
  selectPerksState,
  (state) => state.updatingUser.loaded
);

export const selectPosts = createSelector(selectPerksState, (state) => state.posts.value);

export const selectPostEntities = createSelector(selectPosts, (posts) =>
  posts ? postAdapterSelectors.selectEntities(posts) : {}
);

export const selectAllPosts = createSelector(selectPosts, (posts) =>
  posts ? postAdapterSelectors.selectAll(posts) : []
);
export const selectPostsLoading = createSelector(selectPerksState, (state) => state.posts.loading);
export const selectPostsLoaded = createSelector(selectPerksState, (state) => state.posts.loaded);
export const selectPostsPage = createSelector(selectPerksState, (state) => state.postsPage);
export const selectPostsTotal = createSelector(selectPosts, (posts) =>
  posts ? postAdapterSelectors.selectTotal(posts) : 0
);
export const selectPostById = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id));

export const selectPopularPostsState = createSelector(
  selectPerksState,
  (state) => state.popularPosts.value
);
export const selectPopularPosts = createSelector(selectPopularPostsState, (popularPosts) =>
  popularPosts ? postAdapterSelectors.selectAll(popularPosts) : []
);
export const selectPopularPostsLoading = createSelector(
  selectPerksState,
  (state) => state.popularPosts.loading
);
export const selectPopularPostsLoaded = createSelector(
  selectPerksState,
  (state) => state.popularPosts.loaded
);

export const selectMerch = createSelector(selectPerks, (perks) => perks?.products);
export const selectVideos = createSelector(selectPerks, (perks) => perks?.videos);
export const selectAlbums = createSelector(selectPerks, (perks) => perks?.albums);
export const selectCollectibles = createSelector(selectPerks, (perks) => perks?.collectibles);
export const selectCustomContents = createSelector(selectPerks, (perks) => perks?.customContents);
export const selectCustomContentsById = (id: string) =>
  createSelector(selectCustomContents, (customContents) =>
    customContents?.find((c) => c.id === id)
  );
export const selectCustomContentHtml = (id: string) =>
  createSelector(selectPerksState, (state) => state.customContentHtml[id]);
export const selectCodes = createSelector(selectPerks, (perks) => perks?.codes);

export const selectPostCounts = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id)?.counts?.value);
export const selectPostCountsLoading = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id)?.counts?.loading);
export const selectPostCountsLoaded = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id)?.counts?.loaded);

export const selectTags = createSelector(selectPerksState, (state) => state.tags.value);
export const selectTagsLoading = createSelector(selectPerksState, (state) => state.tags.loading);
export const selectTagsLoaded = createSelector(selectPerksState, (state) => state.tags.loaded);

export const selectAuthors = createSelector(selectPerksState, (state) => state.authors.value);
export const selectAuthorsLoading = createSelector(
  selectPerksState,
  (state) => state.authors.loading
);
export const selectAuthorsLoaded = createSelector(
  selectPerksState,
  (state) => state.authors.loaded
);

export const selectTabContentLast = (contentType: ContentType) =>
  (state: RootState) =>
    state.perks.tabContentPages[contentType]?.last ?? true;

export const selectTabContentNextCursor = (contentType: ContentType) =>
  (state: RootState) =>
    state.perks.tabContentPages[contentType]?.nextCursor ?? null;

export const selectPostComments = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id)?.comments?.value);
export const selectPostCommentsLoading = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id)?.comments?.loading);
export const selectPostCommentsLoaded = (id: string) =>
  createSelector(selectAllPosts, (post) => post.find((p) => p.id === id)?.comments?.loaded);
export const selectPostCommentsLoadRequestParentCommentId = createSelector(
  selectPerksState,
  (state) => state.commentLoadRequest?.parentCommentId
);
export const selectPostCommentsReplyTo = createSelector(selectPerksState, (state) => state.replyTo);
